// Deterministic diagnosis: picks the single weakest measured area and explains it in cautious language.
// Browser measurements cannot see inside the user's network, so copy says "may", "can indicate", "a likely cause".
import type { NormalizedMetrics } from '../measurement/normalize';
import type { MetricKey } from '../scoring/config';
import type { HealthScore } from '../scoring/score';

export type FindingId = 'healthy' | 'busy-latency' | 'high-latency' | 'slow-download' | 'slow-upload' | 'unstable';
export type Severity = 'none' | 'mild' | 'significant';

export interface Diagnosis {
  id: FindingId;
  severity: Severity;
  /** The one main finding shown to the user. */
  finding: string;
  /** The one recommended action. */
  action: string;
  /** Measured facts behind the finding (debug view). */
  evidence: string[];
  confidence: 'medium' | 'low';
}

/** Sub-scores at or above this are not called out. */
export const ISSUE_BELOW = 75;
/** Sub-scores below this are described as a significant issue rather than a mild one. */
export const SIGNIFICANT_BELOW = 50;
/** Ties go to the area that most affects how the connection feels. */
const PRIORITY: MetricKey[] = ['responsiveness', 'latency', 'download', 'upload', 'jitter'];

const FINDING_FOR: Record<MetricKey, Exclude<FindingId, 'healthy'>> = {
  responsiveness: 'busy-latency',
  latency: 'high-latency',
  download: 'slow-download',
  upload: 'slow-upload',
  jitter: 'unstable',
};

export const FINDING_COPY: Record<
  Exclude<FindingId, 'healthy'>,
  Record<'mild' | 'significant', { finding: string; action: string }>
> = {
  'busy-latency': {
    significant: {
      finding: 'Latency increases significantly when the network is busy.',
      action:
        'If video calls lag while someone is downloading, your router or connection may be struggling under load. If your router has a QoS or “Smart Queue” setting, turning it on often helps.',
    },
    mild: {
      finding: 'Your connection is good, but responsiveness drops somewhat when the network is busy.',
      action: 'If calls lag during big downloads or uploads, try pausing other heavy use during the call.',
    },
  },
  'high-latency': {
    significant: {
      finding: 'Your connection takes a long time to respond, so pages, calls and games may feel sluggish.',
      action:
        'If you’re on Wi-Fi, try moving closer to your router and test again. A VPN can also add delay — if you use one, try a check without it.',
    },
    mild: {
      finding: 'Your connection responds a little slowly.',
      action: 'If you’re on Wi-Fi, moving closer to your router may help. A VPN can also add delay.',
    },
  },
  'slow-download': {
    significant: {
      finding: 'Your download speed is low, so streaming and large downloads may be slow.',
      action:
        'Try testing again closer to your router or on a wired connection. If it stays low, your plan or provider may be the limit.',
    },
    mild: {
      finding: 'Your connection works, but download speed is on the slower side for a busy household.',
      action: 'If several people stream at once, test closer to your router to see whether Wi-Fi is limiting speed.',
    },
  },
  'slow-upload': {
    significant: {
      finding: 'Your upload speed is low, so video calls and sending files may struggle.',
      action:
        'Try testing closer to your router. If upload stays low, your internet plan may only offer limited upload speed.',
    },
    mild: {
      finding: 'Your connection is good overall, but upload speed is modest.',
      action: 'If video calls or cloud backups feel slow, check whether your plan offers faster upload.',
    },
  },
  unstable: {
    significant: {
      finding: 'Your connection’s response time varies a lot, which can cause choppy video calls and lag in games.',
      action: 'Wi-Fi interference is a common cause. Try a spot closer to your router or a wired connection, then test again.',
    },
    mild: {
      finding: 'Your connection’s response time is slightly uneven.',
      action: 'If calls occasionally stutter, try testing closer to your router.',
    },
  },
};

export const HEALTHY_COPY = {
  finding: 'Your connection looks healthy: fast and responsive for everyday use, including streaming and video calls.',
  action: 'No action needed. If something feels slow later, run another check at that moment to compare.',
};

const round = (n: number) => (n >= 10 ? Math.round(n) : Math.round(n * 10) / 10);

function evidenceFor(metrics: NormalizedMetrics): string[] {
  const lines: string[] = [];
  if (metrics.downloadMbps !== undefined) lines.push(`Download ${round(metrics.downloadMbps)} Mbps`);
  if (metrics.uploadMbps !== undefined) lines.push(`Upload ${round(metrics.uploadMbps)} Mbps`);
  if (metrics.latencyMs !== undefined) lines.push(`Idle latency ${Math.round(metrics.latencyMs)} ms`);
  if (metrics.jitterMs !== undefined) lines.push(`Jitter ${round(metrics.jitterMs)} ms`);
  if (metrics.loadedLatencyMs !== undefined && metrics.latencyIncreaseMs !== undefined) {
    lines.push(
      `Latency while downloading ${Math.round(metrics.loadedLatencyMs)} ms (+${Math.round(metrics.latencyIncreaseMs)} ms)`,
    );
  } else {
    lines.push('Latency under load not measured');
  }
  return lines;
}

export function diagnose(metrics: NormalizedMetrics, health: HealthScore): Diagnosis {
  const evidence = evidenceFor(metrics);
  // Without a loaded-latency measurement the "healthy" verdict cannot cover behaviour under load.
  const confidence = metrics.latencyIncreaseMs === undefined ? 'low' : 'medium';

  const issues = (Object.entries(health.components) as [MetricKey, { subScore: number }][])
    .filter(([, c]) => c.subScore < ISSUE_BELOW)
    .sort((a, b) => a[1].subScore - b[1].subScore || PRIORITY.indexOf(a[0]) - PRIORITY.indexOf(b[0]));

  const weakest = issues[0];
  if (!weakest) return { id: 'healthy', severity: 'none', ...HEALTHY_COPY, evidence, confidence };

  const [key, component] = weakest;
  const id = FINDING_FOR[key];
  const severity = component.subScore < SIGNIFICANT_BELOW ? 'significant' : 'mild';
  const copy = FINDING_COPY[id][severity];

  const fast =
    (health.components.download?.subScore ?? 0) >= 90 && (health.components.upload?.subScore ?? 0) >= 85;
  const finding =
    id === 'busy-latency' && severity === 'significant' && fast
      ? 'Your connection is fast, but latency increases significantly when the network is busy.'
      : copy.finding;
  return { id, severity, finding, action: copy.action, evidence, confidence };
}
